import { useState, useEffect, useRef, useCallback } from 'react';
import { CheckCircle, XCircle, Loader2, ExternalLink, Copy, Clock, Ban, History, Server as ServerIcon, ChevronDown, ChevronUp, Cloud, GitBranch, Eye, Rocket } from 'lucide-react';
import {
  startOnboarding,
  resumeOnboarding,
  createOnboardingStream,
  cancelOnboarding,
  getOnboardingHistory,
  codeGenDryRun,
} from '../../services/tenantOnboardingApi';

const TENANT_NAME_REGEX = /^[a-z0-9-]+$/;

const PHASE_LABELS = {
  infrastructure: 'Infrastructure',
  database: 'Database',
  codegen: 'Code Generation',
  config: 'App Configuration',
};

const PHASE_ORDER = ['infrastructure', 'database', 'codegen', 'config'];

const RESOURCE_TYPE_LABELS = {
  'Microsoft.Web/sites': 'App Service',
  'Microsoft.Web/serverfarms': 'App Service Plan',
  'Microsoft.Cache/redis': 'Redis Cache',
  'Microsoft.SignalRService/signalR': 'SignalR',
  'Microsoft.Insights/components': 'App Insights',
  'Microsoft.Storage/storageAccounts': 'Storage',
  'Microsoft.Cdn/profiles': 'CDN',
  'Microsoft.Network/frontDoors': 'Front Door',
  'Microsoft.Sql/servers/databases': 'SQL Database',
  'Microsoft.OperationalInsights/workspaces': 'Log Analytics',
};

const MODE_LABELS = { all: 'Full', resources: 'Resources', devops: 'DevOps' };

// ─── Shared UI helpers ──────────────────────────────────────────────────────

const StatusIcon = ({ status, size = 'w-5 h-5' }) => {
  switch (status) {
    case 'succeeded': case 'completed': case 'success': return <CheckCircle className={`${size} text-green-500 shrink-0`} />;
    case 'failed': return <XCircle className={`${size} text-red-500 shrink-0`} />;
    case 'inProgress': return <Loader2 className={`${size} text-blue-500 animate-spin shrink-0`} />;
    case 'cancelled': return <Ban className={`${size} text-yellow-500 shrink-0`} />;
    case 'skipped': return <Ban className={`${size} text-gray-400 shrink-0`} />;
    default: return <div className={`${size} rounded-full border-2 border-gray-300 dark:border-slate-600 shrink-0`} />;
  }
};

const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="ml-2 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400">
      {copied ? 'Copied!' : <Copy className="w-3 h-3 inline" />}
    </button>
  );
};

const statusColor = (s) => ({
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  cancelled: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  inProgress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  pending: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
}[s] || 'bg-gray-100 text-gray-800');

const inputCls = 'w-full px-3 py-2 border rounded-lg dark:bg-slate-800 dark:text-gray-100 dark:border-slate-600 focus:ring-2 focus:ring-blue-500 outline-none';
const compactInputCls = 'px-3 py-2 border rounded-lg dark:bg-slate-800 dark:text-gray-100 dark:border-slate-600 outline-none';

const autoEnumName = (s) => {
  if (!s) return '';
  return s.length <= 3
    ? s.toUpperCase()
    : s.charAt(0).toUpperCase() + s.slice(1).replace(/-./g, (m) => m[1].toUpperCase());
};

// ─── Step progress renderer (shared between tabs) ───────────────────────────

function StepProgress({ steps, overallStatus, statusMessage, expandedResources, toggleResources }) {
  const stepsByPhase = PHASE_ORDER.map((phase) => ({
    phase,
    label: PHASE_LABELS[phase],
    steps: steps.filter((s) => s.phase === phase),
  })).filter((g) => g.steps.length > 0);

  return (
    <>
      <div className="space-y-6">
        {stepsByPhase.map(({ phase, label, steps: phaseSteps }) => (
          <div key={phase}>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{label}</h3>
            <div className="space-y-2">
              {phaseSteps.map((step) => (
                <div key={step.id} className="bg-gray-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-start gap-3 p-3">
                    <StatusIcon status={step.status} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{step.id}</span>
                        {step.url && (
                          <a href={step.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400">
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                      {step.message && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{step.message}</p>}
                      {step.resources?.length > 0 && (
                        <button onClick={() => toggleResources(step.id)}
                          className="flex items-center gap-1 mt-1.5 text-xs text-blue-600 dark:text-blue-400 hover:underline">
                          <ServerIcon className="w-3 h-3" />
                          <span>{step.resources.length} resources deployed</span>
                          {expandedResources[step.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                  </div>
                  {step.resources?.length > 0 && expandedResources[step.id] && (
                    <div className="px-3 pb-3 pt-0 ml-8">
                      <div className="border border-gray-200 dark:border-slate-700 rounded-md overflow-hidden">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                              <th className="text-left px-3 py-1.5 font-medium">Name</th>
                              <th className="text-left px-3 py-1.5 font-medium">Type</th>
                              <th className="text-left px-3 py-1.5 font-medium">State</th>
                            </tr>
                          </thead>
                          <tbody>
                            {step.resources.map((r, idx) => (
                              <tr key={idx} className="border-t border-gray-200 dark:border-slate-700">
                                <td className="px-3 py-1.5 font-mono text-gray-800 dark:text-gray-200">{r.name}</td>
                                <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">{RESOURCE_TYPE_LABELS[r.type] || r.type}</td>
                                <td className="px-3 py-1.5">
                                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                    r.provisioningState === 'Succeeded'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  }`}>{r.provisioningState || 'Unknown'}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {step.subSteps?.length > 0 && (
                    <div className="px-3 pb-3 pt-1 ml-8 space-y-1">
                      {step.subSteps.map((sub, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs">
                          <StatusIcon status={sub.status === 'success' ? 'succeeded' : sub.status} size="w-3.5 h-3.5" />
                          <span className="font-medium text-gray-700 dark:text-gray-300">{sub.name}</span>
                          {sub.url && (
                            <a href={sub.url} target="_blank" rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-0.5">
                              <ExternalLink className="w-3 h-3" /> <span>PR</span>
                            </a>
                          )}
                          {sub.message && <span className="text-gray-400 dark:text-gray-500 ml-auto truncate max-w-xs">{sub.message}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {steps.length === 0 && overallStatus === 'inProgress' && (
          <div className="flex items-center gap-3 p-4 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" /> <span>Connecting to provisioning stream...</span>
          </div>
        )}
      </div>
      {statusMessage && (
        <div className={`mt-4 p-4 rounded-lg text-sm ${
          overallStatus === 'completed' ? 'bg-green-50 dark:bg-green-950 text-green-800 dark:text-green-200' :
          overallStatus === 'cancelled' ? 'bg-yellow-50 dark:bg-yellow-950 text-yellow-800 dark:text-yellow-200' :
          'bg-red-50 dark:bg-red-950 text-red-800 dark:text-red-200'
        }`}>{statusMessage}</div>
      )}
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════════════════════

function TenantOnboarding() {
  const [activeTab, setActiveTab] = useState('resources'); // 'resources' | 'devops' | 'history'

  // ─── Shared form state ────────────────────────────────────────────────────
  const [form, setForm] = useState({
    tenantSlug: '', tenantDisplayName: '', tenantKey: '', enumName: '',
    enableSimulcast: true, address: '', city: '', state: '', zip: '',
    email: '', phone: '', website: '',
  });
  const [formTouched, setFormTouched] = useState(false);

  // ─── Resource provisioning state ──────────────────────────────────────────
  const [resSessionId, setResSessionId] = useState(null);
  const [resSteps, setResSteps] = useState([]);
  const [resOverallStatus, setResOverallStatus] = useState(null);
  const [resStatusMessage, setResStatusMessage] = useState('');
  const resEventSourceRef = useRef(null);
  const [resExpandedResources, setResExpandedResources] = useState({});
  const [resSimulcastApiName, setResSimulcastApiName] = useState(null);

  // ─── DevOps provisioning state ────────────────────────────────────────────
  const [devopsSessionId, setDevopsSessionId] = useState(null);
  const [devopsSteps, setDevopsSteps] = useState([]);
  const [devopsOverallStatus, setDevopsOverallStatus] = useState(null);
  const [devopsStatusMessage, setDevopsStatusMessage] = useState('');
  const devopsEventSourceRef = useRef(null);
  const [devopsExpandedResources, setDevopsExpandedResources] = useState({});
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [dryRunError, setDryRunError] = useState(null);
  const [expandedDryRunRepo, setExpandedDryRunRepo] = useState(null);

  // ─── History state ────────────────────────────────────────────────────────
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedSession, setExpandedSession] = useState(null);
  const [historyExpandedResources, setHistoryExpandedResources] = useState({});

  // ─── Derived ──────────────────────────────────────────────────────────────
  const slug = form.tenantSlug.trim().toLowerCase();
  const slugValid = slug.length >= 3 && slug.length <= 20 && TENANT_NAME_REGEX.test(slug);
  const formValid = slugValid && form.tenantDisplayName.trim() && form.tenantKey > 10000 && form.enumName.trim();
  const devopsFormValid = slugValid && form.tenantKey > 10000 && form.enumName.trim();

  useEffect(() => () => {
    resEventSourceRef.current?.close();
    devopsEventSourceRef.current?.close();
  }, []);

  const handleFormChange = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'tenantSlug') {
        const s = value.trim().toLowerCase();
        if (!prev.enumName || prev.enumName === autoEnumName(prev.tenantSlug))
          updated.enumName = autoEnumName(s);
      }
      return updated;
    });
  };

  // ─── SSE connector (reusable) ─────────────────────────────────────────────

  const connectToStream = useCallback((sid, setSteps, setOverallStatus, setStatusMessage, eventSourceRef, onStepData) => {
    eventSourceRef.current?.close();
    const es = createOnboardingStream(sid);
    eventSourceRef.current = es;

    es.addEventListener('step', (e) => {
      const data = JSON.parse(e.data);
      if (onStepData) onStepData(data);
      setSteps((prev) => {
        const update = { status: data.status, message: data.message, url: data.url, phase: data.phase };
        if (data.resources) update.resources = data.resources;
        if (data.subSteps) update.subSteps = data.subSteps;
        const existing = prev.find((s) => s.id === data.stepId);
        if (existing) return prev.map((s) => s.id === data.stepId ? { ...s, ...update } : s);
        return [...prev, { id: data.stepId, ...update }];
      });
    });

    es.addEventListener('substep', (e) => {
      const data = JSON.parse(e.data);
      setSteps((prev) =>
        prev.map((s) => s.id !== data.stepId ? s : { ...s, subSteps: [...(s.subSteps || []), data.subStep] })
      );
    });

    es.addEventListener('complete', (e) => { setOverallStatus('completed'); setStatusMessage(JSON.parse(e.data).message); es.close(); });
    es.addEventListener('failed', (e) => { setOverallStatus('failed'); setStatusMessage(JSON.parse(e.data).message); es.close(); });
    es.addEventListener('cancelled', (e) => { setOverallStatus('cancelled'); setStatusMessage(JSON.parse(e.data).message); es.close(); });
    es.addEventListener('error', () => {
      setOverallStatus((prev) => prev === 'inProgress' ? 'failed' : prev);
      setStatusMessage((prev) => prev || 'Connection lost');
      es.close();
    });
  }, []);

  // ─── Resource Provisioning handlers ───────────────────────────────────────

  const buildResourceRequest = () => ({
    tenantSlug: slug,
    tenantDisplayName: form.tenantDisplayName.trim(),
    tenantKey: parseInt(form.tenantKey),
    enumName: form.enumName.trim(),
    enableSimulcast: form.enableSimulcast,
    mode: 'resources',
    address: form.address || null, city: form.city || null,
    state: form.state || null, zip: form.zip || null,
    email: form.email || null, phone: form.phone || null,
    website: form.website || null,
  });

  const handleResourceStart = async () => {
    setFormTouched(true);
    if (!formValid) return;
    try {
      const res = await startOnboarding(buildResourceRequest());
      setResSessionId(res.sessionId);
      setResOverallStatus('inProgress');
      setResStatusMessage('');
      connectToStream(res.sessionId, setResSteps, setResOverallStatus, setResStatusMessage, resEventSourceRef, (data) => {
        if (data.simulcastApiName) setResSimulcastApiName(data.simulcastApiName);
      });
    } catch (err) { setResOverallStatus('failed'); setResStatusMessage(err.message); }
  };

  const handleResourceResume = async () => {
    try {
      setResSteps([]); setResStatusMessage('');
      const res = await resumeOnboarding(buildResourceRequest());
      setResSessionId(res.sessionId);
      setResOverallStatus('inProgress');
      connectToStream(res.sessionId, setResSteps, setResOverallStatus, setResStatusMessage, resEventSourceRef, (data) => {
        if (data.simulcastApiName) setResSimulcastApiName(data.simulcastApiName);
      });
    } catch (err) { setResOverallStatus('failed'); setResStatusMessage(err.message); }
  };

  const handleResourceCancel = async () => {
    if (resSessionId) try { await cancelOnboarding(resSessionId); } catch { /* best-effort */ }
  };

  // ─── DevOps Provisioning handlers ─────────────────────────────────────────

  const handleDryRun = async () => {
    setFormTouched(true);
    if (!devopsFormValid) return;
    setDryRunLoading(true); setDryRunError(null); setDryRunResult(null);
    try {
      const result = await codeGenDryRun({ tenantSlug: slug, tenantKey: parseInt(form.tenantKey), enumName: form.enumName.trim() });
      setDryRunResult(result);
    } catch (err) { setDryRunError(err.message); }
    finally { setDryRunLoading(false); }
  };

  const handleDevopsStart = async () => {
    setFormTouched(true);
    if (!devopsFormValid) return;
    try {
      const res = await startOnboarding({
        tenantSlug: slug,
        tenantDisplayName: form.tenantDisplayName.trim() || form.enumName.trim(),
        tenantKey: parseInt(form.tenantKey),
        enumName: form.enumName.trim(),
        mode: 'devops',
      });
      setDevopsSessionId(res.sessionId);
      setDevopsOverallStatus('inProgress');
      setDevopsStatusMessage('');
      setDryRunResult(null);
      connectToStream(res.sessionId, setDevopsSteps, setDevopsOverallStatus, setDevopsStatusMessage, devopsEventSourceRef);
    } catch (err) { setDevopsOverallStatus('failed'); setDevopsStatusMessage(err.message); }
  };

  const handleDevopsCancel = async () => {
    if (devopsSessionId) try { await cancelOnboarding(devopsSessionId); } catch { /* best-effort */ }
  };

  // ─── History ──────────────────────────────────────────────────────────────

  const loadHistory = async () => {
    setHistoryLoading(true);
    try { setHistory(await getOnboardingHistory(30)); }
    catch { setHistory([]); }
    finally { setHistoryLoading(false); }
  };

  useEffect(() => { if (activeTab === 'history') loadHistory(); }, [activeTab]);

  // ─── Shared form renderer ─────────────────────────────────────────────────

  const renderTenantIdentityFields = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tenant Slug *</label>
        <input value={form.tenantSlug} onChange={(e) => handleFormChange('tenantSlug', e.target.value.toLowerCase())}
          placeholder="e.g. acme" className={inputCls} />
        {formTouched && !slugValid && <span className="text-xs text-red-500">3-20 chars, lowercase, numbers, hyphens</span>}
        {slugValid && <span className="text-xs text-gray-500">RG: <strong>{slug}-rsg-prod</strong></span>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">TenantKey *</label>
        <input type="number" value={form.tenantKey} onChange={(e) => handleFormChange('tenantKey', e.target.value)}
          placeholder="e.g. 10023" className={inputCls} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Enum Name *</label>
        <input value={form.enumName} onChange={(e) => handleFormChange('enumName', e.target.value)}
          placeholder="e.g. Acme" className={inputCls} />
      </div>
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // Render
  // ═════════════════════════════════════════════════════════════════════════

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button onClick={() => setActiveTab(id)}
      className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-1.5 ${
        activeTab === id ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700'
      }`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Tenant Onboarding</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">Provision resources and generate DevOps code independently.</p>
        </div>
        <div className="flex gap-2">
          <TabBtn id="resources" icon={Cloud} label="Resource Provisioning" />
          <TabBtn id="devops" icon={GitBranch} label="DevOps Provisioning" />
          <TabBtn id="history" icon={History} label="History" />
        </div>
      </div>

      {/* ═══ Resource Provisioning Tab ═══ */}
      {activeTab === 'resources' && (
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
          {!resOverallStatus && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">Resource Provisioning</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Creates Azure infrastructure, provisions database, pushes connection strings and environment variables.
                </p>
              </div>

              {renderTenantIdentityFields()}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Name *</label>
                  <input value={form.tenantDisplayName} onChange={(e) => handleFormChange('tenantDisplayName', e.target.value)}
                    placeholder="e.g. Acme Auction Group" className={inputCls} />
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-slate-700 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Company Info</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input value={form.address} onChange={(e) => handleFormChange('address', e.target.value)} placeholder="Address" className={compactInputCls} />
                  <input value={form.city} onChange={(e) => handleFormChange('city', e.target.value)} placeholder="City" className={compactInputCls} />
                  <input value={form.state} onChange={(e) => handleFormChange('state', e.target.value)} placeholder="State" className={compactInputCls} />
                  <input value={form.zip} onChange={(e) => handleFormChange('zip', e.target.value)} placeholder="Zip" className={compactInputCls} />
                  <input value={form.email} onChange={(e) => handleFormChange('email', e.target.value)} placeholder="Email" className={compactInputCls} />
                  <input value={form.phone} onChange={(e) => handleFormChange('phone', e.target.value)} placeholder="Phone" className={compactInputCls} />
                  <input value={form.website} onChange={(e) => handleFormChange('website', e.target.value)} placeholder="Website URL" className={`md:col-span-2 ${compactInputCls}`} />
                </div>
              </div>

              <div className="flex items-center gap-4 pt-4">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={form.enableSimulcast} onChange={(e) => handleFormChange('enableSimulcast', e.target.checked)} className="rounded" />
                  Enable Simulcast
                </label>
                <button onClick={handleResourceStart} disabled={!formValid}
                  className="ml-auto px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors">
                  Start Provisioning
                </button>
              </div>
            </div>
          )}

          {resOverallStatus && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">Provisioning: {slug}</h2>
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${statusColor(resOverallStatus)}`}>{resOverallStatus}</span>
                </div>
                <div className="flex gap-2">
                  {resOverallStatus === 'inProgress' && (
                    <button onClick={handleResourceCancel} className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 text-sm transition-colors">Cancel</button>
                  )}
                  {resOverallStatus === 'failed' && (
                    <button onClick={handleResourceResume} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 text-sm transition-colors">Resume (skip existing)</button>
                  )}
                  {resOverallStatus !== 'inProgress' && (
                    <button onClick={() => { setResOverallStatus(null); setResSessionId(null); setResSteps([]); setResStatusMessage(''); setResSimulcastApiName(null); }}
                      className="px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-slate-700 text-sm transition-colors">
                      New Provisioning
                    </button>
                  )}
                </div>
              </div>
              {resSimulcastApiName && resSimulcastApiName !== `${slug}-api` && (
                <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-sm flex items-start gap-2">
                  <span className="font-medium shrink-0">Fallback:</span>
                  <span>
                    Primary name <code className="font-mono bg-amber-100 dark:bg-amber-900 px-1 rounded">{slug}-api</code> was unavailable.
                    Using <code className="font-mono bg-amber-100 dark:bg-amber-900 px-1 rounded font-bold">{resSimulcastApiName}</code> for all simulcast API references.
                  </span>
                </div>
              )}
              <StepProgress steps={resSteps} overallStatus={resOverallStatus} statusMessage={resStatusMessage}
                expandedResources={resExpandedResources} toggleResources={(id) => setResExpandedResources((p) => ({ ...p, [id]: !p[id] }))} />
            </div>
          )}
        </div>
      )}

      {/* ═══ DevOps Provisioning Tab ═══ */}
      {activeTab === 'devops' && (
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
          {!devopsOverallStatus && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">DevOps Provisioning</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Generates pipeline YAML and enum code changes across all repos, creates branches and PRs.
                </p>
              </div>

              {renderTenantIdentityFields()}

              <div className="flex items-center gap-3 pt-4">
                <button onClick={handleDryRun} disabled={!devopsFormValid || dryRunLoading}
                  className="px-5 py-2 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center gap-2">
                  {dryRunLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                  Preview Changes
                </button>
                <button onClick={handleDevopsStart} disabled={!devopsFormValid}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2">
                  <Rocket className="w-4 h-4" /> Generate PRs
                </button>
              </div>

              {dryRunError && (
                <div className="mt-4 p-4 rounded-lg text-sm bg-red-50 dark:bg-red-950 text-red-800 dark:text-red-200">{dryRunError}</div>
              )}

              {dryRunResult && (
                <div className="mt-4 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Preview: {dryRunResult.repos?.length || 0} repos will be modified
                  </h3>
                  <div className="space-y-2">
                    {dryRunResult.repos?.map((repo) => (
                      <div key={repo.repoName} className="border border-gray-200 dark:border-slate-700 rounded-lg">
                        <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800"
                          onClick={() => setExpandedDryRunRepo(expandedDryRunRepo === repo.repoName ? null : repo.repoName)}>
                          <StatusIcon status={repo.status === 'success' ? 'succeeded' : repo.status === 'dry-run' ? 'pending' : repo.status} />
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{repo.repoName}</span>
                          <span className="text-xs text-gray-500 ml-auto">{repo.files?.length || 0} files</span>
                          {expandedDryRunRepo === repo.repoName ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </div>
                        {expandedDryRunRepo === repo.repoName && repo.files?.length > 0 && (
                          <div className="px-3 pb-3 space-y-2">
                            {repo.files.map((file, idx) => (
                              <div key={idx} className="bg-gray-50 dark:bg-slate-800 rounded p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${{
                                    enum: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
                                    pipeline: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                                    buildall: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
                                    buildlist: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
                                    envfile: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                                  }[file.changeType] || 'bg-gray-100 text-gray-800'}`}>{file.changeType}</span>
                                  <span className="text-xs font-mono text-gray-600 dark:text-gray-400">{file.filePath}</span>
                                </div>
                                {file.description && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{file.description}</p>
                                )}
                                {file.changeType === 'enum' && file.enumName && (
                                  <pre className="text-xs bg-green-50 dark:bg-green-950 text-green-800 dark:text-green-200 p-2 rounded overflow-x-auto">
                                    <div>+ {file.enumName} = {file.tenantKey}</div>
                                  </pre>
                                )}
                                {file.changeType === 'pipeline' && file.appServiceName && (
                                  <pre className="text-xs bg-green-50 dark:bg-green-950 text-green-800 dark:text-green-200 p-2 rounded overflow-x-auto max-h-48">
                                    <div>+ parameter: {file.paramName}</div>
                                    <div>+ appServiceName: '{file.appServiceName}'</div>
                                    <div>+ resourceGroup: '{file.resourceGroup}'</div>
                                    <div>+ template: {file.templatePath}</div>
                                  </pre>
                                )}
                                {file.changeType === 'buildall' && (
                                  <pre className="text-xs bg-green-50 dark:bg-green-950 text-green-800 dark:text-green-200 p-2 rounded overflow-x-auto">
                                    <div>+ parameter: {file.paramName}</div>
                                    <div>+ job: build_{file.tenantSlug}</div>
                                  </pre>
                                )}
                                {file.changeType === 'buildlist' && (
                                  <pre className="text-xs bg-green-50 dark:bg-green-950 text-green-800 dark:text-green-200 p-2 rounded overflow-x-auto">
                                    <div>+ - {file.tenantSlug}</div>
                                  </pre>
                                )}
                                {file.changeType === 'envfile' && (
                                  <pre className="text-xs bg-green-50 dark:bg-green-950 text-green-800 dark:text-green-200 p-2 rounded overflow-x-auto max-h-32">
                                    <div>+ new file: {file.filePath}</div>
                                  </pre>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {devopsOverallStatus && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">DevOps: {slug}</h2>
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${statusColor(devopsOverallStatus)}`}>{devopsOverallStatus}</span>
                </div>
                <div className="flex gap-2">
                  {devopsOverallStatus === 'inProgress' && (
                    <button onClick={handleDevopsCancel} className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 text-sm transition-colors">Cancel</button>
                  )}
                  {devopsOverallStatus !== 'inProgress' && (
                    <button onClick={() => { setDevopsOverallStatus(null); setDevopsSessionId(null); setDevopsSteps([]); setDevopsStatusMessage(''); setDryRunResult(null); setDryRunError(null); }}
                      className="px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-slate-700 text-sm transition-colors">
                      New Generation
                    </button>
                  )}
                </div>
              </div>
              <StepProgress steps={devopsSteps} overallStatus={devopsOverallStatus} statusMessage={devopsStatusMessage}
                expandedResources={devopsExpandedResources} toggleResources={(id) => setDevopsExpandedResources((p) => ({ ...p, [id]: !p[id] }))} />
            </div>
          )}
        </div>
      )}

      {/* ═══ History Tab ═══ */}
      {activeTab === 'history' && (
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">Provisioning History</h2>
            <button onClick={loadHistory} disabled={historyLoading}
              className="px-3 py-1 text-sm bg-gray-100 dark:bg-slate-800 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-400">
              {historyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
            </button>
          </div>

          {history.length === 0 && !historyLoading && <p className="text-sm text-gray-500">No provisioning sessions found.</p>}

          <div className="space-y-2">
            {history.map((session) => (
              <div key={session.sessionId} className="border border-gray-200 dark:border-slate-700 rounded-lg">
                <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800"
                  onClick={() => setExpandedSession(expandedSession === session.sessionId ? null : session.sessionId)}>
                  <StatusIcon status={session.overallStatus} />
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{session.tenantSlug}</span>
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${statusColor(session.overallStatus)}`}>{session.overallStatus}</span>
                  {session.mode && session.mode !== 'all' && (
                    <span className="text-xs px-2 py-0.5 rounded font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                      {MODE_LABELS[session.mode] || session.mode}
                    </span>
                  )}
                  <span className="text-xs text-gray-500 ml-auto flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {new Date(session.startedAt).toLocaleString()}
                  </span>
                  {expandedSession === session.sessionId ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>

                {expandedSession === session.sessionId && (
                  <div className="px-3 pb-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-slate-800 rounded p-2">
                      <span>TenantKey: <strong>{session.tenantKey}</strong></span>
                      <span>Enum: <strong>{session.enumName}</strong></span>
                      <span>Display: <strong>{session.tenantDisplayName}</strong></span>
                      <span>Session: <strong>{session.sessionId}</strong></span>
                      {session.simulcastApiName && (
                        <span className="col-span-2">Simulcast API: <strong className={session.simulcastApiName !== `${session.tenantSlug}-api` ? 'text-amber-600 dark:text-amber-400' : ''}>
                          {session.simulcastApiName}</strong>
                          {session.simulcastApiName !== `${session.tenantSlug}-api` && <span className="ml-1 text-amber-500">(fallback)</span>}
                        </span>
                      )}
                    </div>
                    {session.connectionString && (
                      <div className="text-xs bg-gray-50 dark:bg-slate-800 rounded p-2">
                        Connection String <CopyBtn text={session.connectionString} />
                        <code className="block mt-1 text-gray-600 dark:text-gray-400 break-all">
                          {session.connectionString.replace(/Password=[^;]+/, 'Password=***')}
                        </code>
                      </div>
                    )}
                    <div className="space-y-1">
                      {session.steps?.map((step, i) => (
                        <div key={i}>
                          <div className="flex items-center gap-2 text-xs">
                            <StatusIcon status={step.status} size="w-4 h-4" />
                            <span className="text-gray-600 dark:text-gray-400">{step.id}</span>
                            {step.resources?.length > 0 && (
                              <button onClick={(e) => { e.stopPropagation(); setHistoryExpandedResources((p) => ({ ...p, [`${session.sessionId}-${step.id}`]: !p[`${session.sessionId}-${step.id}`] })); }}
                                className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline">
                                <ServerIcon className="w-3 h-3" /> <span>{step.resources.length} resources</span>
                              </button>
                            )}
                            {step.message && <span className="text-gray-400 ml-auto truncate max-w-sm">{step.message}</span>}
                            {step.url && <a href={step.url} target="_blank" rel="noopener noreferrer" className="text-blue-500"><ExternalLink className="w-3 h-3" /></a>}
                          </div>
                          {step.resources?.length > 0 && historyExpandedResources[`${session.sessionId}-${step.id}`] && (
                            <div className="ml-6 mt-1 mb-2">
                              <div className="border border-gray-200 dark:border-slate-700 rounded-md overflow-hidden">
                                <table className="w-full text-xs">
                                  <thead><tr className="bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                                    <th className="text-left px-2 py-1 font-medium">Name</th>
                                    <th className="text-left px-2 py-1 font-medium">Type</th>
                                    <th className="text-left px-2 py-1 font-medium">State</th>
                                  </tr></thead>
                                  <tbody>{step.resources.map((r, idx) => (
                                    <tr key={idx} className="border-t border-gray-200 dark:border-slate-700">
                                      <td className="px-2 py-1 font-mono text-gray-800 dark:text-gray-200">{r.name}</td>
                                      <td className="px-2 py-1 text-gray-500 dark:text-gray-400">{RESOURCE_TYPE_LABELS[r.type] || r.type}</td>
                                      <td className="px-2 py-1">
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                          r.provisioningState === 'Succeeded' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                        }`}>{r.provisioningState || 'Unknown'}</span>
                                      </td>
                                    </tr>
                                  ))}</tbody>
                                </table>
                              </div>
                            </div>
                          )}
                          {step.subSteps?.length > 0 && (
                            <div className="ml-6 mt-1 mb-1 space-y-0.5">
                              {step.subSteps.map((sub, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-xs">
                                  <StatusIcon status={sub.status === 'success' ? 'succeeded' : sub.status} size="w-3 h-3" />
                                  <span className="text-gray-600 dark:text-gray-400">{sub.name}</span>
                                  {sub.url && <a href={sub.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline flex items-center gap-0.5"><ExternalLink className="w-3 h-3" /> <span>PR</span></a>}
                                  {sub.message && <span className="text-gray-400 ml-auto truncate max-w-xs">{sub.message}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default TenantOnboarding;
